import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  Injector,
  viewChild,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Capacitor } from '@capacitor/core';
import { Router, Scroll } from '@angular/router';
import { filter, take, takeUntil, timer } from 'rxjs';
import { I18NextModule } from 'angular-i18next';
import { MatButtonModule } from '@angular/material/button';
import { IconComponent } from 'src/app/common/icon/icon.component';
import { RouteTranslatePipe } from '../../../pipes/route-translate.pipe';
import { SearchBarStore } from '../search-bar.store';
import { SearchInputDirective } from '../search-input.directive';
import { SearchResultsComponent } from '../search-results/search-results.component';
import { SearchSuggestionsComponent } from '../search-suggestions/search-suggestions.component';

let nextId = 0;
const KEYBOARD_MIN_HEIGHT = 150;

@Component({
  selector: 'sharry-search-bar-mobile',
  standalone: true,
  imports: [
    I18NextModule,
    IconComponent,
    MatButtonModule,
    RouteTranslatePipe,
    SearchInputDirective,
    SearchResultsComponent,
    SearchSuggestionsComponent,
  ],
  templateUrl: './search-bar-mobile.component.html',
  styleUrl: './search-bar-mobile.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchBarMobileComponent {
  readonly store = inject(SearchBarStore);
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);
  private readonly input = viewChild<ElementRef<HTMLInputElement>>('input');
  private readonly bar = viewChild<ElementRef<HTMLFormElement>>('bar');
  private readonly body = viewChild<ElementRef<HTMLElement>>('body');
  private readonly overlay = viewChild<ElementRef<HTMLElement>>('overlay');
  private readonly legacyOverscroll = typeof CSS !== 'undefined' && !CSS.supports('overscroll-behavior', 'contain');

  readonly inputId = `search-bar-mobile-input-${nextId}`;
  readonly listboxId = `search-bar-mobile-listbox-${nextId++}`;
  private scrollLocked = false;
  private scrollY = 0;
  private lockedUrl = '';
  private stopViewportWatch?: () => void;

  constructor() {
    effect(
      () => {
        const open = this.store.isOpen() && this.store.isMobile();
        if (open !== this.scrollLocked) {
          this.lockPageScroll(open);
          this.watchViewport(open);
          this.scrollLocked = open;
        }
        if (open) {
          afterNextRender(
            () => {
              this.moveOverlayToBody();
              this.input()?.nativeElement.focus({ preventScroll: true });
            },
            { injector: this.injector },
          );
        }
      },
      { allowSignalWrites: true },
    );
  }

  onSubmit(event: Event) {
    event.preventDefault();
    if (!this.store.canSubmit()) return;
    this.store.submit();
    this.input()?.nativeElement.blur();
  }

  clear() {
    this.store.setQuery('');
    this.input()?.nativeElement.focus();
  }

  onTouchMove(event: TouchEvent) {
    this.closeKeyboardOutsideBar(event);
    if (!this.legacyOverscroll) return;
    const body = this.body()?.nativeElement;
    const scrollsBody = body?.contains(event.target as Node) && body.scrollHeight > body.clientHeight;
    if (!scrollsBody && event.cancelable) event.preventDefault();
  }

  keepBodyScrollable() {
    const body = this.body()?.nativeElement;
    if (!this.legacyOverscroll || !body) return;
    const max = body.scrollHeight - body.clientHeight;
    if (max <= 1) return;
    if (body.scrollTop <= 0) body.scrollTop = 1;
    else if (body.scrollTop >= max) body.scrollTop = max - 1;
  }

  closeKeyboardOutsideBar(event: TouchEvent) {
    if (this.bar()?.nativeElement.contains(event.target as Node)) return;
    const input = this.input()?.nativeElement;
    if (input && this.document.activeElement === input) input.blur();
  }

  private watchViewport(watch: boolean) {
    this.stopViewportWatch?.();
    this.stopViewportWatch = undefined;
    const window = this.document.defaultView;
    const visualViewport = window?.visualViewport;
    if (!watch || !visualViewport) {
      this.store.keyboardOpen.set(false);
      return;
    }
    const sync = () => {
      const keyboardOpen = window.innerHeight - visualViewport.height - visualViewport.offsetTop > KEYBOARD_MIN_HEIGHT;
      this.store.keyboardOpen.set(keyboardOpen);
      if (!keyboardOpen && window.scrollY !== 0) window.scrollTo(0, 0);
    };
    visualViewport.addEventListener('resize', sync);
    visualViewport.addEventListener('scroll', sync);
    sync();
    this.stopViewportWatch = () => {
      visualViewport.removeEventListener('resize', sync);
      visualViewport.removeEventListener('scroll', sync);
    };
  }

  private moveOverlayToBody() {
    const overlay = this.overlay()?.nativeElement;
    if (overlay && Capacitor.isNativePlatform() && overlay.parentElement !== this.document.body) {
      this.document.body.appendChild(overlay);
    }
  }

  private lockPageScroll(lock: boolean) {
    const { body, defaultView } = this.document;
    if (lock) {
      this.scrollY = defaultView.scrollY;
      this.lockedUrl = this.router.url;
      Object.assign(body.style, {
        position: 'fixed',
        top: `-${this.scrollY}px`,
        left: '0',
        right: '0',
        overflow: 'hidden',
      });
    } else {
      Object.assign(body.style, { position: '', top: '', left: '', right: '', overflow: '' });
      const y = this.scrollY;
      const restore = () => defaultView.scrollTo(0, y);
      restore();
      this.router.events
        .pipe(
          filter((event): event is Scroll => event instanceof Scroll),
          take(1),
          takeUntil(timer(1000)),
        )
        .subscribe(event => {
          const url =
            'urlAfterRedirects' in event.routerEvent ? event.routerEvent.urlAfterRedirects : event.routerEvent.url;
          if (url === this.lockedUrl) setTimeout(restore);
        });
    }
  }
}
